// PMReportButton.jsx
import React, { useContext, useState } from "react";
import { Button } from "@mui/material";
import jsPDF from "jspdf";
import "jspdf-autotable";
import PropertyContext from "../../contexts/PropertyContext";
import { firestore as db } from "../../firebase";
import { collectPMChecks, fetchPMChecks } from "../../utils/reports/pmData";
import { downloadBlob } from "../../utils/reports/downloadBlob";
import { buildReportFilename } from "../../utils/reports/reportFilename";
import { drawReportHeader, stampPageNumbers } from "../../utils/reports/pdfChrome";
import ReportDateRangeDialog from "./shared/ReportDateRangeDialog";

/**
 * Props:
 * - rooms: rooms array from PropertyContext
 * - currentUserName: string (who generated the report)
 *
 * Scans rooms for PM logs in the selected date range, fetches the matching PM
 * check docs, and builds a PDF (cover page + one landscape page per room/check).
 */
export default function PMReportButton({ rooms, currentUserName = "Unknown" }) {
  const [open, setOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const { selectedProperty, selectedClient } = useContext(PropertyContext);

  // Convert nested values to printable strings
  const toCell = (val) => {
    if (val === null || val === undefined) return "";
    if (
      typeof val === "string" ||
      typeof val === "number" ||
      typeof val === "boolean"
    ) {
      return String(val);
    }
    try {
      return JSON.stringify(val, null, 2);
    } catch {
      return String(val);
    }
  };

  const addCoverPage = (doc, meta, rows) => {
    const { generatedBy, generatedAt, start, end } = meta;

    const contentTop = drawReportHeader(doc, {
      title: "Preventative Maintenance Report",
      dateRange: { start, end },
      generatedBy,
      generatedAt,
    });

    const tableBody = rows.map((r) => [
      r.roomName,
      r.performedBy || "-",
      r.pmDate.toLocaleString(),
    ]);

    doc.autoTable({
      startY: contentTop,
      head: [["Room", "Performed By", "PM Date"]],
      body: tableBody,
      styles: { fontSize: 10, cellPadding: 4 },
      headStyles: { fillColor: [30, 30, 30] },
      margin: { left: 40, right: 40 },
      theme: "striped",
    });
  };

  const addRoomPageLandscape = (doc, entry) => {
    doc.addPage("a4", "landscape");

    const left = 40;
    let y = 50;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(`Room ${entry.roomName}`, left, y);
    y += 20;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    const performedBy =
      entry.pmDoc?.performedBy ||
      entry.pmDoc?.userName ||
      entry.pmDoc?.createdBy ||
      "-";
    const pmDateStr = entry.pmDate?.toLocaleString() || "-";

    doc.text(`PM Date: ${pmDateStr}`, left, y);
    y += 16;
    doc.text(`Performed By: ${performedBy}`, left, y);
    y += 24;

    if (entry.pmDoc) {
      const rows = Object.entries(entry.pmDoc)
        .filter(([k]) => !["id"].includes(k))
        .map(([k, v]) => [k, toCell(v)]);

      doc.autoTable({
        startY: y,
        head: [["Field", "Value"]],
        body: rows,
        styles: { fontSize: 9, cellPadding: 3, valign: "top" },
        headStyles: { fillColor: [30, 30, 30] },
        margin: { left, right: 40 },
        theme: "grid",
        columnStyles: {
          0: { cellWidth: 160 },
          1: { cellWidth: "auto" },
        },
      });
    } else {
      doc.setFont("helvetica", "italic");
      doc.text("No PM data found for this check ID.", left, y);
    }
  };

  const handleGenerate = async ({ start, end }) => {
    setIsGenerating(true);
    try {
      // 1) Gather all matches in range
      const matches = collectPMChecks(rooms, start, end);
      if (!matches.length) {
        alert("No preventative maintenance logs found in that date range.");
        return;
      }

      // 2) Fetch all pmCheck docs
      const withDocs = await fetchPMChecks(db, selectedClient, selectedProperty, matches);

      // 3) Prepare cover rows
      const coverRows = withDocs.map((m) => ({
        roomName: m.roomName,
        performedBy:
          m.pmDoc?.performedBy ||
          m.pmDoc?.userName ||
          m.pmDoc?.createdBy ||
          "-",
        pmDate: m.pmDate,
      }));

      // 4) Build PDF
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "pt",
        format: "a4",
      });

      addCoverPage(doc, {
        generatedBy: currentUserName,
        generatedAt: new Date(),
        start,
        end,
      }, coverRows);

      // 5) Add a landscape page per room/check
      withDocs.forEach((entry) => addRoomPageLandscape(doc, entry));

      stampPageNumbers(doc);

      const blob = doc.output("blob");
      downloadBlob(blob, buildReportFilename("PM_Report", { ext: "pdf", start, end }));
      setOpen(false);
    } catch (e) {
      console.error(e);
      alert(
        "There was an error generating the report. Check console for details."
      );
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <>
      <Button variant="contained" fullWidth onClick={() => setOpen(true)}>
        PM Report
      </Button>

      <ReportDateRangeDialog
        open={open}
        title="Generate PM Report"
        description="Scans all rooms for PM logs in range, fetches PM check docs, and generates a PDF with a cover page plus one page per room."
        busy={isGenerating}
        onClose={() => setOpen(false)}
        onSubmit={handleGenerate}
      />
    </>
  );
}
